/**
 * Explorer Timeline Component
 *
 * Vertical scrollable timeline showing historical civic moments
 * Inline accordion-style expansion for event details
 * Line segments between nodes (no line through circles)
 * Glow effects on hover/active
 * Tapping the circular image opens an enlarged image lightbox via portal
 */

import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, Calendar, X } from 'lucide-react';
import { TimelineEvent, getTimelineImageUrl } from '../lib/explorerTimelineService';

interface ExplorerTimelineProps {
  events: TimelineEvent[];
}

interface LightboxState {
  imageUrl: string;
  alt: string;
}

function TimelineImageLightbox({ imageUrl, alt, onClose }: LightboxState & { onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999, animation: 'tl-lb-fadeIn 180ms ease-out' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged timeline image"
    >
      {/* Full-screen dark overlay with blur */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      />

      {/* Close button - large touch target for mobile */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 z-20 flex items-center justify-center rounded-full transition-all duration-150 active:scale-90"
        style={{
          width: '44px',
          height: '44px',
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(4px)',
        }}
        aria-label="Close image"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Image container - stops click propagation so tapping image doesn't close */}
      <div
        className="relative z-10 flex flex-col items-center px-4"
        style={{ animation: 'tl-lb-scaleIn 250ms cubic-bezier(0.16, 1, 0.3, 1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageUrl}
          alt={alt}
          className="rounded-lg shadow-2xl"
          style={{
            maxWidth: '88vw',
            maxHeight: '70vh',
            objectFit: 'contain',
          }}
          draggable={false}
        />
        <p
          className="mt-3 text-center text-[13px] truncate max-w-[80vw]"
          style={{ color: 'rgba(200, 200, 200, 0.85)' }}
        >
          {alt}
        </p>
      </div>

      <style>{`
        @keyframes tl-lb-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes tl-lb-scaleIn {
          from { opacity: 0; transform: scale(0.88); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>,
    document.body
  );
}

export default function ExplorerTimeline({ events }: ExplorerTimelineProps) {
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[14px] text-[#A0A0A0]" style={{ lineHeight: '1.55' }}>
          No timeline events yet.
        </p>
      </div>
    );
  }

  const handleEventClick = (eventId: string) => {
    setExpandedEventId(prev => prev === eventId ? null : eventId);
  };

  const handleImageClick = (e: React.MouseEvent, imageUrl: string, alt: string) => {
    e.stopPropagation();
    e.preventDefault();
    setLightbox({ imageUrl, alt });
  };

  const closeLightbox = useCallback(() => {
    setLightbox(null);
  }, []);

  return (
    <div className="relative">
      {/* Timeline events */}
      <div className="space-y-5">
        {events.map((event, index) => {
          const imageUrl = event.image_path ? getTimelineImageUrl(event.image_path) : null;
          const isLast = index === events.length - 1;
          const isExpanded = expandedEventId === event.id;

          return (
            <div key={event.id} className="relative">
              <div
                onClick={() => handleEventClick(event.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleEventClick(event.id);
                  }
                }}
                className="relative flex items-center gap-4 w-full text-left cursor-pointer group focus:outline-none"
                tabIndex={0}
                role="button"
                aria-expanded={isExpanded}
              >
                {/* Node circle with solid background to mask line */}
                <div
                  className={`relative flex-shrink-0 z-10 transition-all duration-150 ease-out ${
                    isExpanded ? 'scale-[1.08]' : 'md:group-hover:scale-[1.08]'
                  }`}
                  style={{ width: '40px', height: '40px' }}
                >
                  {/* Solid background circle to mask the line behind */}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      backgroundColor: '#1A1A1A',
                      zIndex: 1,
                    }}
                  />

                  {/* Border ring - thicker and more visible */}
                  <div
                    className="absolute inset-0 rounded-full transition-all duration-180 ease-out"
                    style={{
                      border: '1.5px solid rgba(180, 180, 180, 0.8)',
                      zIndex: 3,
                    }}
                  />

                  {/* Circle content - clickable image opens lightbox */}
                  {imageUrl ? (
                    <div
                      className="absolute inset-0 rounded-full overflow-hidden cursor-zoom-in active:scale-[0.9] transition-transform duration-100"
                      style={{
                        backgroundColor: '#0A0A0A',
                        zIndex: 4,
                      }}
                      onClick={(e) => handleImageClick(e, imageUrl, event.title)}
                      role="button"
                      aria-label={`View image for ${event.title}`}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          setLightbox({ imageUrl, alt: event.title });
                        }
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    </div>
                  ) : (
                    <div
                      className="absolute inset-0 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: 'rgba(60, 60, 60, 0.5)',
                        zIndex: 2,
                      }}
                    >
                      <Calendar className="w-4 h-4 text-gray-500" />
                    </div>
                  )}

                  {/* Active glow - only when expanded */}
                  {isExpanded && (
                    <div
                      className="absolute inset-0 rounded-full transition-all duration-200 ease-out pointer-events-none"
                      style={{
                        boxShadow: `
                          0 0 10px 2px rgba(64, 224, 208, 0.4),
                          0 0 20px 4px rgba(64, 224, 208, 0.35),
                          0 0 30px 6px rgba(64, 224, 208, 0.25)
                        `,
                        zIndex: 3,
                      }}
                    />
                  )}
                </div>

                {/* Event text */}
                <div className="flex-1 z-10 min-w-0">
                  <div
                    className="text-[14px] leading-tight transition-colors duration-150"
                    style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    <span
                      className={`font-bold transition-colors duration-150 ${
                        isExpanded
                          ? 'text-white'
                          : 'md:group-hover:text-white'
                      }`}
                      style={{ color: isExpanded ? '#FFFFFF' : '#F0F0F0' }}
                    >
                      {event.year}
                    </span>
                    <span className="mx-2">—</span>
                    <span
                      className={`font-normal transition-colors duration-150 ${
                        isExpanded
                          ? 'text-[rgba(255,255,255,0.9)]'
                          : 'md:group-hover:text-[rgba(255,255,255,0.85)]'
                      }`}
                    >
                      {event.title}
                    </span>
                  </div>
                </div>

                {/* Chevron - consistent neutral grey color */}
                <div className="flex-shrink-0 z-10 transition-transform duration-200">
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-[#808080]" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[#808080]" />
                  )}
                </div>
              </div>

              {/* Inline expanded detail */}
              {isExpanded && (
                <div
                  className="overflow-hidden transition-all duration-200 ease-out"
                  style={{
                    animation: 'expandDetail 200ms ease-out',
                  }}
                >
                  <div
                    className="expanded-detail-card mt-4 ml-[56px] rounded-lg border shadow-sm"
                    style={{
                      backgroundColor: '#222222',
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '14px 16px',
                    }}
                  >
                    <p
                      className="text-[13px] text-[#D0D0D0]"
                      style={{ lineHeight: '1.65' }}
                    >
                      {event.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Always-visible base line segment (extends to next node or detail card) */}
              {!isLast && (
                <div
                  className="absolute left-[20px] w-[2px]"
                  style={{
                    top: '40px',
                    height: isExpanded ? 'calc(100% + 20px)' : 'calc(100% + 20px)',
                    backgroundColor: 'rgba(64, 224, 208, 0.2)',
                    zIndex: 0,
                  }}
                />
              )}

              <style>{`
                @keyframes expandDetail {
                  from {
                    opacity: 0;
                    transform: translateY(-8px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
                @media (max-width: 640px) {
                  .expanded-detail-card {
                    margin-left: 52px !important;
                    padding: 12px 14px !important;
                  }
                }
              `}</style>
            </div>
          );
        })}
      </div>

      {/* Image Lightbox - rendered via portal at document.body to escape all parent stacking contexts */}
      {lightbox && (
        <TimelineImageLightbox
          imageUrl={lightbox.imageUrl}
          alt={lightbox.alt}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
}
